import { useRef, useCallback } from 'react';

/**
 * Purpose of elementOffsetRatio
 * The elementOffsetRatio serves as a correction factor for vertical mouse movement during image resizing.
 * Here's what it does:
 *
 * How it's calculated:
 * ```
 * const elementRect = element.getBoundingClientRect();
 * const heightDelta = Math.abs(Math.floor(elementRect.height) - Math.floor(height));
 * const bottomDelta = Math.abs(Math.floor(elementRect.bottom) - Math.floor(bottom));
 *
 * elementOffsetRatio.current = bottomDelta === 0 ? 1 : bottomDelta / heightDelta;
 * ```
 *
 * What it compensates for:
 * The ratio compensates for situations where:
 * - Expected behavior: When you drag the mouse by N pixels, the element should resize by N pixels
 * - Actual behavior: Due to CSS constraints, positioning, or layout context, the element's actual
 *   size change doesn't match the mouse movement
 *
 * Examples:
 * - Normal case: ratio = 1 → Mouse movement directly translates to size change
 * - Constrained case: ratio = 0.5 → Element grows less than expected, so mouse movement is
 *   amplified (y / 0.5 = 2*y)
 * - Amplified case: ratio = 2 → Element grows more than expected, so mouse movement is
 *   reduced (y / 2 = 0.5*y)
 *
 * It's necessary for these reasons:
 * - Visual Consistency: Without this correction, resizing would feel "broken" to users - dragging
 *   20px might result in 10px or 40px of actual size change, creating a poor UX.
 * - Layout Context Dependencies: Images in different layout contexts (grids, flex containers, positioned
 *   elements) behave differently during resize. This ratio accounts for those differences.
 * - Complex Interaction Cases: The correction is especially important for:
 *   - Positioned images
 *   - Images in composite layouts
 *   - Gallery/slider images
 *   - Multi-element resize operations
 * * */
const useOffsetRatio = (initialValue = 1) => {
  const offsetRatioRef = useRef(initialValue);

  const setOffsetRatio = useCallback((newValue) => {
    offsetRatioRef.current = newValue;
  }, []);

  return [offsetRatioRef, setOffsetRatio];
};

export default useOffsetRatio;
